/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdShare, IoMdMore } from "react-icons/io";
import { FaFileDownload, FaArrowLeft } from "react-icons/fa";
import { IoIosHome } from "react-icons/io";
import { signOut, getCurrentUser, fetchUserAttributes, fetchAuthSession } from "aws-amplify/auth";
import "./Header.css";
import GlueLogo from "../glueLogo";
import {
  exportAsSVG,
  exportAsPNG,
  exportAsPDF,
} from "../LandingPage/TreeGraph/exportChart";
import ExportExcel from "../LandingPage/TreeGraph/ExportExcel";
import { Analytics, resetAnalyticsIdentity } from "../../api/analytics";
import { useParams } from "react-router-dom";

const Header = ({
  navigateTo,
  headerName,
  layout,
  setLayout,
  dataLength,
  data,
  searchTerm,
  onSearchChange,
  onSearchSubmit,
  setDownloadOpen,
}) => {

  const location = useLocation();
  const navigate = useNavigate();

  const { glue_id } = useParams();
  const glueId = glue_id ? decodeURIComponent(glue_id) : null;

  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMoreOptions, setIsMoreOptions] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const isSummary = location.pathname.includes("/summary");
  const isDashboard = location.pathname.includes("/glue-dashboard");

  const showOrientation = layout && dataLength < 50;

  // ⭐ Analytics helper with glueId included
  const track = (eventName, extra = {}) => {
    Analytics.logEvent(eventName, {
      glueId: glueId || null,
      ...extra,
    });
  };

  // --------------------------
  // USER SETUP
  // --------------------------
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();

        const session = await fetchAuthSession();

        // ✅ FIXED GROUP DETECTION (Amplify v6)
        const groups =
          session.tokens?.idToken?.payload?.["cognito:groups"] ||
          session.tokens?.accessToken?.payload?.["cognito:groups"] ||
          [];

        resetAnalyticsIdentity();
        localStorage.setItem("userId", attributes.email);
        setUserInfo({
          username: user.username,
          email: attributes.email,
          givenName: attributes.given_name,
          familyName: attributes.family_name,
          isAdmin: groups.includes("Admin"),
          isPaidUser: groups.includes("Paywall"),
        });
      } catch (err) {
        console.error("Error fetching user info:", err);
      }
    };

    fetchUser();
  }, []);

  // --------------------------
  // LOGOUT
  // --------------------------
  const handleLogout = async () => {
    try {
      track("user_logged_out", { user: localStorage.getItem("userId") });

      localStorage.removeItem("userId");
      sessionStorage.setItem("disclaimerAccepted", "false");

      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // --------------------------
  // SHARE
  // --------------------------
  const handleShare = (e) => {
    e.stopPropagation();
    track("share_clicked");

    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // --------------------------
  // PRINT DOWNLOAD
  // --------------------------
  const handleDownload = () => {
    track("download_excel", { method: "browser_print", glueId });

    setDownloadOpen(true);
    setTimeout(() => window.print(), 100);
  };

  // --------------------------
  // MENU TOGGLES
  // --------------------------
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const toggleMoreDropdown = () => {
    setIsMoreOptions(!isMoreOptions);
  };

  // --------------------------
  // BACK / HOME BUTTON
  // --------------------------
  const handleBack = () => {
    if (isSummary) track("back_button_clicked");
    else track("home_button_clicked");

    navigateTo ? navigate(navigateTo) : navigate(-1);
  };

  // --------------------------
  // ORIENTATION CHANGE
  // --------------------------
  const handleOrientationChange = (value) => {
    track("orientation_changed", { value });
    setLayout(value);
  };

  // --------------------------
  // RENDER
  // --------------------------
  return (
    <header className="header">
      <div className="top-bar">
        <div className="logo">
          <div
            className="logo-with-back"
            onClick={() => {
              track("navigate_dashboard");
              isDashboard ? navigate("/") : navigate("/glue-dashboard");
            }}
          >
            <h3>
              <GlueLogo height="45" />
            </h3>
          </div>
        </div>

        {userInfo && (
          <div className="profile-section">
            <div className="profile-info" onClick={toggleProfileDropdown}>
              <div className="default-profile-icon">👤</div>
              <span className="profile-name">
                {userInfo.givenName || userInfo.familyName
                  ? `${userInfo.givenName || ""} ${userInfo.familyName || ""}`
                  : userInfo.username}
              </span>
              <span className="dropdown-arrow">▾</span>
            </div>

            {isProfileDropdownOpen && (
              <div className="profile-dropdown">

                {userInfo?.isAdmin && (
                  <a
                    onClick={() => navigate("/analytics")}
                    style={{ cursor: "pointer", textAlign: "center" }}
                  >
                    Analytics Dashboard
                  </a>
                )}

                {userInfo?.isAdmin && (
                  <a
                    onClick={() => navigate("/admin-dashboard")}
                    style={{ cursor: "pointer", textAlign: "center" }}
                  >
                    Admin Dashboard
                  </a>
                )}

                <a
                  onClick={handleLogout}
                  style={{ cursor: "pointer", textAlign: "center" }}
                >
                  Logout
                </a>

              </div>
            )}

          </div>
        )}
      </div>

      {/* SUB HEADER */}
      <div className="sub-bar">

        {/* LEFT — BACK BUTTON */}
        <div className="sub-bar-left">
          <button className="back-button" onClick={handleBack}>
            {isSummary ? <FaArrowLeft size={25} /> : isDashboard ? null : <IoIosHome size={25} />}
          </button>

          <h3 className="advanced-search">{headerName}</h3>
        </div>

        {/* CENTER — SEARCH */}
        {layout && (
          <form
            className="search-bar-graph"
            onSubmit={(e) => {
              e.preventDefault();
              onSearchSubmit();
            }}
          >
            <input
              type="text"
              className="search-input-graph"
              placeholder="Search Entities..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            <button type="submit" className="search-button-graph">
              <svg viewBox="0 0 24 24">
                <path d="M15.5 14h-.79..." />
              </svg>
            </button>
          </form>
        )}

        {/* RIGHT — SHARE / DOWNLOAD / ACTIONS */}
        {isDashboard ? null : (
          <div className="sub-bar-right">
            {isSummary ? (
              <div className="controls">
                <div className="tooltip-container">
                  <IoMdShare className="iconHeader" onClick={handleShare} size={25} />
                  <span className="tooltip-text">Share</span>
                  {copied && <span className="copy-tooltip">Copied!</span>}
                </div>

                {userInfo?.isPaidUser ?
                  <div className="tooltip-container">
                    <FaFileDownload
                      className={`iconHeader ${!userInfo?.isPaidUser ? "locked-icon" : ""}`}
                      onClick={userInfo?.isPaidUser ? handleDownload : null}
                      size={25}
                    />
                    <span className="tooltip-text">
                      Download
                    </span>
                  </div>
                  :
                  <div className="tooltip-container">
                    <a
                      href="https://www.glueid.com/sales"
                      style={{ textDecoration: 'none' }}
                    >
                      <FaFileDownload
                        className={"iconHeader"}
                        size={25}
                        color="#fff"
                      />
                      <span className="tooltip-text">

                        Upgrade to Download
                      </span>
                    </a>
                  </div>
                }
              </div>
            ) : (
              <div className="orientation-toggle">
                {showOrientation && (
                  <>
                    <label><strong>Orientation:</strong></label>

                    <label>
                      <input
                        type="radio"
                        name="layout"
                        value="vertical"
                        checked={layout === "vertical"}
                        onChange={() => handleOrientationChange("vertical")}
                      />
                      Vertical
                    </label>

                    <label style={{ marginLeft: 12 }}>
                      <input
                        type="radio"
                        name="layout"
                        value="horizontal"
                        checked={layout === "horizontal"}
                        onChange={() => handleOrientationChange("horizontal")}
                      />
                      Horizontal
                    </label>
                  </>
                )}

                {/* MORE MENU */}
                <div className="tooltip-container" style={{ marginLeft: 12 }}>
                  <IoMdMore
                    className="iconHeader"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMoreDropdown();
                    }}
                    size={25}
                  />
                  <span className="tooltip-text">Actions</span>

                  {isMoreOptions && (
                    <div
                      className="share-menu"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button onClick={handleShare}>Share</button>

                      <button
                        onClick={() => {
                          track("download_png");
                          exportAsPNG("mychart");
                        }}
                      >
                        Download PNG
                      </button>

                      <button
                        onClick={() => {
                          track("download_pdf");
                          exportAsPDF("mychart");
                        }}
                      >
                        Download PDF
                      </button>

                      <button
                        onClick={() => {
                          track("download_svg");
                          exportAsSVG("mychart");
                        }}
                      >
                        Download SVG
                      </button>

                      <ExportExcel
                        jsonData={data}
                        onClick={() => track("download_excel")}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
